// Web oturum store'u — Octo.Mobile authStore deseninin web uyarlaması.
// Access token yalnızca bellekte; refresh token tokenStorage'da; tek-uçuşlu refresh tokenProvider üzerinden.
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { apiClient } from '@/shared/api/http/client';
import { dataApi } from '@/shared/api/dataApi';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { bindTokenProvider } from '@/shared/api/http/tokenProvider';
import { runtimeConfig } from '@/app/config/runtimeConfig';
import type { AuthLoginResponse, LoginResponse } from '@/shared/types/api';
import { tokenStorage } from '../lib/tokenStorage';
import { impersonation } from '../lib/impersonation';
import { clientInfo, isUsableSessionId, resolveSessionId, sessionAppName } from '../lib/sessionRecord';
import { addBreadcrumb } from '@/shared/lib/logging/clientLog';
import { disablePush } from '@/shared/lib/push/pushClient';

export type AuthStatus = 'booting' | 'anonymous' | 'authenticated' | 'expired';
export type LogoutReason = 'manual' | 'expired' | 'tenant' | 'session-closed' | 'license';

export interface SessionUser {
  userId: string;
  legacyUserId: number | null;
  userCode: string;
  userName: string;
  roleId: string | null;
  roleName: string | null;
  isSysRol: boolean;
  canUseAiChat: boolean;
  canUseChat: boolean;
  homeDashboardId: string | null;
  hasWebLicense: boolean;
  hasNodeLicense: boolean;
  prefersNodeView: boolean;
  requiresPasswordChange: boolean;
  isImpersonating: boolean;
  /** Taklit oturumunu açan yönetici (#64) — şeritte "yönetici: …" olarak görünür. */
  impersonatorUserName: string | null;
  profileImage: string | null;
}

export interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  accessTokenExp: number | null;
  user: SessionUser | null;
  connectionId: string | null;
  connectionName: string | null;
  sessionId: string | null;
  rememberMe: boolean;
  lastLogoutReason: LogoutReason | null;
  /**
   * Oturumu düşüren girişin cihaz bilgisi (ClientInfo). Uyarıda ve giriş ekranında
   * gösterilir: "başka bir yerden giriş yapıldı" cümlesinin ilk sorusu "nereden?".
   */
  lastLogoutDevice: string | null;

  login: (args: {
    userCode: string;
    password: string;
    remember: boolean;
    connectionId?: string | null;
    connectionName?: string | null;
  }) => Promise<void>;
  bootstrap: () => Promise<void>;
  refresh: () => Promise<string | null>;
  logout: (reason?: LogoutReason) => Promise<void>;
  setProfileImage: (b64: string | null) => void;
  /**
   * Profil fotoğrafını sunucudan çeker.
   *
   * Fotoğraf giriş yanıtında BİLEREK yok (Blazor'da yanıt auth cookie'sine claim olarak
   * yazılıyor ve gömülü byte[] cookie'yi başlık sınırının üstüne çıkarıyordu). React de
   * aynı yanıtı kullandığı için fotoğrafı ayrıca çekmek zorunda; bu yapılmadığından
   * kullanıcı fotoğrafını yüklüyor, çıkıp girdiğinde avatar boş dönüyordu.
   */
  loadProfileImage: () => Promise<void>;
  setLastLogoutDevice: (info: string | null) => void;
  setPrefersNodeView: (v: boolean) => void;
  clearPasswordChangeFlag: () => void;
}

// Taklit sekmesi (#64) sekmeler arası oturum kanalına katılmaz: yöneticinin giriş/çıkışı onu,
// onun çıkışı da yönetici sekmelerini etkilememeli.
const channel =
  typeof BroadcastChannel !== 'undefined' && !impersonation.isTab()
    ? new BroadcastChannel('octo-auth')
    : null;

/** Taklit sekmesi ilk açılışta oturumunu devir anahtarından alır; alamazsa normal sekmeye döner. */
async function receiveImpersonation(): Promise<void> {
  const id = impersonation.pendingId();
  if (!id || tokenStorage.getSnapshot()) return;
  const data = await impersonation.waitFor(id);
  if (!data?.refreshToken || !data.user || !data.sessionId) {
    impersonation.endTab();
    // Sekmeyi yönetici sekmesi açtı; tarayıcı izin verirse kapanır, vermezse giriş ekranı görünür.
    try {
      window.close();
    } catch {
      /* noop */
    }
    return;
  }
  tokenStorage.setRefreshToken(data.refreshToken, false);
  tokenStorage.setSnapshot(
    {
      user: data.user,
      connectionId: data.connectionId ?? null,
      connectionName: data.connectionName ?? null,
      sessionId: data.sessionId,
      savedAt: Date.now(),
    },
    false,
  );
}

export function mapLoginUser(r: LoginResponse, prev?: SessionUser | null): SessionUser {
  return {
    userId: r.userId ?? prev?.userId ?? '',
    legacyUserId: r.legacyUserId ?? prev?.legacyUserId ?? null,
    userCode: r.kullaniciKodu ?? prev?.userCode ?? '',
    userName: r.kullaniciAdi ?? prev?.userName ?? '',
    roleId: r.roleId ?? prev?.roleId ?? null,
    roleName: r.rolName ?? prev?.roleName ?? null,
    isSysRol: r.isSysRol ?? prev?.isSysRol ?? false,
    canUseAiChat: r.canUseAiChat ?? prev?.canUseAiChat ?? false,
    canUseChat: r.canUseChat ?? prev?.canUseChat ?? false,
    homeDashboardId: r.homeDashboardId ?? prev?.homeDashboardId ?? null,
    hasWebLicense: r.hasWebLicense ?? prev?.hasWebLicense ?? true,
    hasNodeLicense: r.hasNodeLicense ?? prev?.hasNodeLicense ?? false,
    prefersNodeView: r.prefersNodeView ?? prev?.prefersNodeView ?? false,
    requiresPasswordChange: r.requiresPasswordChange ?? prev?.requiresPasswordChange ?? false,
    isImpersonating: r.isImpersonating ?? prev?.isImpersonating ?? false,
    impersonatorUserName: r.impersonatorUserName ?? prev?.impersonatorUserName ?? null,
    profileImage: prev?.profileImage ?? null,
  };
}

function decodeExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Başarısız giriş denemesini kaydeder. Girişi ENGELLEMEZ: kayıt ucu kimlik istemiyor ve
 * hatası yutuluyor — denetim kaydı yüzünden kullanıcı giriş ekranında kalmamalı.
 */
async function recordFailedLogin(
  userCode: string,
  reason: string | null | undefined,
  connectionId: string | null,
) {
  try {
    await apiClient.post(
      ENDPOINTS.REGISTER_FAILED_LOGIN,
      {
        UserId: null,
        SessionId: crypto.randomUUID(),
        AppName: sessionAppName(),
        ClientInfo: await clientInfo(),
        FailureReason: reason ?? 'Bilinmeyen hata',
        AttemptedUserCode: userCode,
      },
      {
        octo: { expectSuccess: false, skipAuth: true },
        headers: connectionId ? { 'X-Octo-Connection': connectionId } : {},
      },
    );
  } catch {
    /* denetim kaydı isteğe bağlı */
  }
}

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    status: 'booting',
    accessToken: null,
    accessTokenExp: null,
    user: null,
    connectionId: null,
    connectionName: null,
    sessionId: null,
    rememberMe: tokenStorage.isRemembered(),
    lastLogoutReason: null,
    lastLogoutDevice: null,

    login: async ({ userCode, password, remember, connectionId = null, connectionName = null }) => {
      const res = await apiClient.post<AuthLoginResponse>(
        ENDPOINTS.AUTH_LOGIN,
        { userCode, password, appId: runtimeConfig().appId },
        {
          octo: { expectSuccess: false },
          headers: connectionId ? { 'X-Octo-Connection': connectionId } : {},
        },
      );
      if (!res.success || !res.accessToken || !res.user) {
        // Başarısız deneme de kayda geçer: "kim, hangi kullanıcı koduyla, nereden"
        // denedi sorusu ancak burada tutulursa cevaplanabiliyor (Blazor kabuğu bunu
        // zaten yapıyordu, React arayüzünde hiç kaydedilmiyordu).
        void recordFailedLogin(userCode, res.message, connectionId);
        throw new Error(res.message ?? 'Giriş başarısız.');
      }
      const user = mapLoginUser(res.user);
      const sessionId = resolveSessionId(res.user.sessionId);
      const connId =
        (res.user as LoginResponse & { connectionId?: string | null }).connectionId ?? connectionId;
      const connName =
        (res.user as LoginResponse & { connectionName?: string | null }).connectionName ?? connectionName;

      tokenStorage.setRemembered(remember);
      tokenStorage.setRefreshToken(res.refreshToken, remember);
      set({
        status: 'authenticated',
        accessToken: res.accessToken,
        accessTokenExp: decodeExp(res.accessToken),
        user,
        connectionId: connId,
        connectionName: connName,
        sessionId,
        rememberMe: remember,
        lastLogoutReason: null,
        // Yeni giriş eski uyarıyı siler; yoksa bir sonraki çıkışta eski cihaz görünürdü.
        lastLogoutDevice: null,
      });
      tokenStorage.setSnapshot(
        { user: res.user, connectionId: connId, connectionName: connName, sessionId, savedAt: Date.now() },
        remember,
      );

      // Oturum kaydı + tek oturum kuralı (hatalar girişi engellemez)
      try {
        await apiClient.post(
          ENDPOINTS.REGISTER_SESSION,
          {
            SessionId: sessionId,
            UserId: user.userId,
            AppName: sessionAppName(),
            ClientInfo: await clientInfo(),
            LoginTime: new Date().toISOString(),
            IsSuccess: true,
          },
          { octo: { expectSuccess: false } },
        );
        await apiClient.get(ENDPOINTS.CLOSE_OLD_SESSIONS(user.userId, sessionId), {
          octo: { expectSuccess: false },
        });
      } catch {
        /* oturum kaydı isteğe bağlı */
      }
      // Fotoğraf ayrı uçtan; girişi bekletmez.
      void get().loadProfileImage();
      addBreadcrumb('oturum', `giriş: ${user.userCode}${connName ? ` @ ${connName}` : ''}`);
      channel?.postMessage({ type: 'login' });
    },

    refresh: async () => {
      const rt = tokenStorage.getRefreshToken();
      if (!rt) return null;
      try {
        const res = await apiClient.post<AuthLoginResponse>(
          ENDPOINTS.AUTH_REFRESH,
          { refreshToken: rt },
          { octo: { expectSuccess: false, skipAuth: true } },
        );
        if (!res.success || !res.accessToken) return null;
        const remember = get().rememberMe;
        tokenStorage.setRefreshToken(res.refreshToken, remember);
        const prev = get().user;
        const user = res.user ? mapLoginUser(res.user, prev) : prev;
        set({
          accessToken: res.accessToken,
          accessTokenExp: decodeExp(res.accessToken),
          user,
          status: 'authenticated',
        });
        channel?.postMessage({ type: 'refresh' });
        return res.accessToken;
      } catch {
        return null;
      }
    },

    bootstrap: async () => {
      if (impersonation.isTab()) await receiveImpersonation();
      const snap = tokenStorage.getSnapshot();
      const rt = tokenStorage.getRefreshToken();
      if (!snap || !rt) {
        tokenStorage.clear();
        set({ status: 'anonymous' });
        return;
      }
      set({
        user: mapLoginUser(snap.user),
        connectionId: snap.connectionId,
        connectionName: snap.connectionName,
        sessionId: snap.sessionId,
      });
      const token = await get().refresh();
      if (!token) {
        tokenStorage.clear();
        set({ status: 'anonymous', user: null, accessToken: null });
        return;
      }
      /*
       * Lisans/tercih alanları refresh yanıtında yok → Kullanici ucu ile tazele.
       *
       * Lisans bayrakları GELEN YANITA GÖRE kurulur, yalnızca doğruya çevrilmez:
       * eski kod `licenseId != null ? true : önceki` yazıyordu ve lisansı KALDIRILAN
       * kullanıcı, anlık görüntüdeki eski "true" ile sayfayı her yenilediğinde web'e
       * girmeye devam ediyordu. Aynı şey hesabı pasife alınan kullanıcı için de
       * geçerliydi. Uç yanıt vermezse (ağ hatası) oturum düşmez — o zaman elde
       * yalnızca anlık görüntü var ve onu doğru kabul etmek tek seçenek.
       */
      try {
        const u = await apiClient.get<Record<string, unknown>>(ENDPOINTS.USER(snap.user.userId ?? ''), {
          octo: { expectSuccess: false },
        });
        if (u && typeof u === 'object') {
          const prev = get().user!;
          const hasKey = (k: string) => Object.prototype.hasOwnProperty.call(u, k);
          const filled = (k: string) => u[k] != null && u[k] !== '';
          const hasWebLicense = hasKey('licenseId') ? filled('licenseId') : prev.hasWebLicense;
          const hasNodeLicense = hasKey('nodeLicenseId') ? filled('nodeLicenseId') : prev.hasNodeLicense;
          const active = hasKey('active') ? u['active'] !== false : true;

          if (!active || (!hasWebLicense && !hasNodeLicense)) {
            tokenStorage.clear();
            set({
              status: 'anonymous',
              user: null,
              accessToken: null,
              accessTokenExp: null,
              sessionId: null,
              lastLogoutReason: 'license',
            });
            return;
          }

          set({
            user: {
              ...prev,
              hasWebLicense,
              hasNodeLicense,
              prefersNodeView:
                typeof u['prefersNodeView'] === 'boolean'
                  ? (u['prefersNodeView'] as boolean)
                  : prev.prefersNodeView,
              canUseAiChat:
                typeof u['canUseAiChat'] === 'boolean' ? (u['canUseAiChat'] as boolean) : prev.canUseAiChat,
              canUseChat:
                typeof u['canUseChat'] === 'boolean' ? (u['canUseChat'] as boolean) : prev.canUseChat,
            },
          });
        }
      } catch {
        /* ağ hatası oturumu düşürmez */
      }

      // Sayfa yenilendiğinde de avatar dolsun (anlık görüntüde fotoğraf tutulmuyor).
      void get().loadProfileImage();
    },

    logout: async (reason = 'manual') => {
      const { sessionId } = get();
      try {
        // Bu tarayıcıya artık bu kullanıcının bildirimleri gitmesin (ortak bilgisayar).
        if (!impersonation.isTab()) await disablePush(false);
        if (isUsableSessionId(sessionId))
          await apiClient.post(ENDPOINTS.CLOSE_SESSION(sessionId!), undefined, {
            octo: { expectSuccess: false },
          });
        await apiClient.post(ENDPOINTS.AUTH_LOGOUT, undefined, { octo: { expectSuccess: false } });
      } catch {
        /* noop */
      }
      addBreadcrumb('oturum', `çıkış: ${reason}`);
      tokenStorage.clear();
      if (impersonation.isTab()) {
        // Taklit bitti: sekme normal sekmeye döner; açan sekme yöneticininki, mümkünse kapanır.
        impersonation.endTab();
        if (reason === 'manual')
          try {
            window.close();
          } catch {
            /* noop */
          }
      }
      set({
        status: reason === 'manual' ? 'anonymous' : 'expired',
        accessToken: null,
        accessTokenExp: null,
        user: null,
        sessionId: null,
        lastLogoutReason: reason,
      });
      channel?.postMessage({ type: 'logout' });
    },

    setProfileImage: (b64) => set((s) => (s.user ? { user: { ...s.user, profileImage: b64 } } : {})),

    loadProfileImage: async () => {
      const userId = get().user?.userId;
      if (!userId) return;
      try {
        const image = await dataApi.getUserProfileImage(userId);
        // Oturum bu arada değişmiş olabilir (çıkış/başka kullanıcı): yanlış avatarı
        // yazmamak için kimlik yeniden kontrol edilir.
        if (get().user?.userId === userId) get().setProfileImage(image);
      } catch {
        /* fotoğraf isteğe bağlı: gelmezse baş harfler gösterilir */
      }
    },
    setLastLogoutDevice: (info) => set({ lastLogoutDevice: info }),
    setPrefersNodeView: (v) => set((s) => (s.user ? { user: { ...s.user, prefersNodeView: v } } : {})),
    clearPasswordChangeFlag: () =>
      set((s) => (s.user ? { user: { ...s.user, requiresPasswordChange: false } } : {})),
  })),
);

// tokenProvider bağlama — axios/SSE/SignalR store'a bağımlı olmadan token okur
bindTokenProvider({
  getAccessToken: () => useAuthStore.getState().accessToken,
  refresh: () => useAuthStore.getState().refresh(),
  onLogout: (reason) => void useAuthStore.getState().logout(reason === 'manual' ? 'manual' : reason),
  getConnectionId: () => useAuthStore.getState().connectionId,
  getSessionId: () => useAuthStore.getState().sessionId,
});

channel?.addEventListener('message', (ev: MessageEvent<{ type: string }>) => {
  const st = useAuthStore.getState();
  if (ev.data?.type === 'logout' && st.status === 'authenticated') {
    tokenStorage.clear();
    useAuthStore.setState({ status: 'anonymous', accessToken: null, user: null, sessionId: null });
  }
  if (ev.data?.type === 'login' && st.status === 'anonymous') void st.bootstrap();
});
